import { Component, Signal, signal } from '@angular/core';
import { LifecycleEventType } from 'src/app/lifecycle-events/models/lifecycle-event-type.enum';
import { ACCRUAL_TYPE_ENUM } from 'src/app/product-capture/model/enums/accrual-type.enum';
import { CallabilityBarrierEnum, CallabilityEnum, KeyDatesEnum, PayoffEnum, ProductTypeEnum, ScheduleType } from 'src/app/product-capture/model/enums/product-capture.enums';
import { SCHEDULE_TYPE_ENUM } from 'src/app/product-capture/model/enums/schedule-type.enum';
import { BarrierType } from 'src/app/static-data/models/consts/barrier-types.const';
import { CALL_NOTICE_LAG_BASE_TYPES, CallSchedulePayload, SCHEDULE_LAG_ITEMS, ScheduleStrategyKeys } from '../../../event-schedules/event-schedules.const';
import { ScheduleComponent } from '../schedule.component';

@Component({
  selector: 'app-call-schedule',
  templateUrl: '../schedule.component.html',
  styleUrls: [
    './call-schedule.component.scss',
    '../schedule.component.scss'
  ],
  providers: [
    { provide: ScheduleComponent, useExisting: CallScheduleComponent }
  ]
})
export class CallScheduleComponent extends ScheduleComponent {

  key = ScheduleType.CALLABILITY;

  schedulePayoffKey = PayoffEnum.callability;

  featureKey = CallabilityEnum.callEventType;

  callNoticeLagBaseItems = CALL_NOTICE_LAG_BASE_TYPES;

  callNoticeLagItems = SCHEDULE_LAG_ITEMS;

  allowedScheduleStrategies = [
    ScheduleStrategyKeys.COUPON_FROM_CALL, 
    ScheduleStrategyKeys.INDEPENDENT_COUPON_AND_CALL, 
    ScheduleStrategyKeys.CALL_ONLY
  ];

  applyDefaultScheduleType(): void {
    const isIssuerCall = this.getCallEventType() === LifecycleEventType.ISSUER_CALL;
    const forbiddenScheduleTypes = isIssuerCall ? [SCHEDULE_TYPE_ENUM.OBS_PRIMARY, SCHEDULE_TYPE_ENUM.INDEPENDENT] : [SCHEDULE_TYPE_ENUM.PAYMENT_ONLY];

    this.scheduleTypeItems = this.scheduleTypeItems.filter((item) => !forbiddenScheduleTypes.includes(item.id))

    if (this.isEditing) {
      return;
    }

    this.scheduleType.setValue(isIssuerCall ? SCHEDULE_TYPE_ENUM.PAYMENT_ONLY : SCHEDULE_TYPE_ENUM.OBS_PRIMARY);
  }

  getSchedulePayload(): CallSchedulePayload {
    const obsFrequency = this.callability.get(CallabilityEnum.barrier)
      ?.get(CallabilityBarrierEnum.obsFrequency)?.value;

    const { schedule, obsRollConfig, ...payload } = this.getScheduleFormValue();
    let { lastPeriodCallable } = this.featureForm.value;

    if (lastPeriodCallable === null) {
      lastPeriodCallable = true;
    }

    let earlyRedemption: CallSchedulePayload = {
      ...payload,
      pmtScheduleLag: payload.pmtScheduleLag || '0B',
      callNoticeLag: payload.callNoticeLag || '0B',
      callNoticeLagBaseType: payload.callNoticeLagBaseType || ACCRUAL_TYPE_ENUM.PAYMENT_ADJUSTED,
      pmtScheduleLagBase: payload.pmtScheduleLagBase || ACCRUAL_TYPE_ENUM.UNADJUSTED,
      type: this.getCallFormValue(CallabilityEnum.callEventType),
      frequency: this.callabilityFrequency,
      pmtRollConvention: this.getRollConvention(
        KeyDatesEnum.settlementRollConvention
      ),
      deferredPeriods: this.getCallFormValue(CallabilityEnum.deferredPeriods, '0'),
      lastPeriodCallable
    };

    if (this.getCallEventType() === 'AUTOCALL') {
      earlyRedemption.barrier = {
        obsScheduleLag: '0B',
        obsScheduleLagBase: ACCRUAL_TYPE_ENUM.PAYMENT_ADJUSTED,
        obsRollConvention: this.getRollConvention(KeyDatesEnum.tradingRollConvention),
        obsFrequency: this.getValueOrFallback(obsFrequency),
        fixFrequency: 'INITIAL_FIXING_DATE',
        ...(obsRollConfig && { obsRollConfig })
      }
    }

    if (this.scheduleStrategy === ScheduleStrategyKeys.CALL_FROM_COUPON) {
      const { pmtScheduleLag, pmtScheduleLagBase, scheduleType } = this.couponScheduleForm.getRawValue();
      earlyRedemption = {
        ...earlyRedemption,
        pmtScheduleLag,
        pmtScheduleLagBase,
        scheduleType,
      }
    }

    return earlyRedemption;
  }

  getColumnsByScheduleType(): {} {
    const callEventType = this.callability.get(
      CallabilityEnum.callEventType
    ).value;

    return {
      barrierLevel: callEventType === ProductTypeEnum.AUTOCALL,
      unadjObsDate:
        this.scheduleTypeValue !== SCHEDULE_TYPE_ENUM.PAYMENT_PRIMARY &&
        this.scheduleTypeValue !== SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY,
      adjObsDate: this.scheduleTypeValue !== SCHEDULE_TYPE_ENUM.PAYMENT_ONLY,
      unadjPmtDate:
        this.scheduleTypeValue !== SCHEDULE_TYPE_ENUM.OBS_PRIMARY &&
        this.scheduleTypeValue !== SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY,
      rateDeterminationDate: false,
      accrualStartDate: false,
      accrualEndDate: false,
      callNoticeDate:
        this.getCallEventType() === LifecycleEventType.ISSUER_CALL,
      redemptionAmountPayable_relative:
        callEventType &&
        this.callability.get(CallabilityEnum.amountType).value ===
          BarrierType.STEPPED,
    };
  }

  override setFieldsVisibility(): void {
    this.fieldsVisiblity = {
      scheduleType: this.getScheduleTypeVisibility(),
      pmtScheduleLag: this.getComputedPaymentLagVisibility(),
      refRateFixingLag: signal(false),
      accrualScheduleLagBase: signal(false),
      callNoticeLag: this.getCallNoticeLagVisibility(),
      lastPeriodCallable: signal(true),
    };
  }

  private getCallNoticeLagVisibility(): Signal<boolean> {
    const callEventTypeControl = this.featureForm.get(this.featureKey);
    return this.showField(callEventTypeControl, (callEventType) => callEventType === 'ISSUER_CALL');
  }

  getCallFormValue(key: CallabilityEnum, fallbackValue = ''): any {
    return this.getValueOrFallback(this.callability?.get(key)?.value, fallbackValue);
  }

  getValueOrFallback(value: any, fallbackValue = ''): any {
    return value || fallbackValue;
  }
}
